import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import I18n from 'i18n-js';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import find from 'lodash/find';
import { Dropdown, Icon, Menu } from 'grab-ui';
import { setShopAction } from 'actions/shopify-actions';
import {
  shopifyShopSelector,
  shopifyShopsSelector,
} from 'selectors/shopify-selectors';
import { ROUTES_V2_PATHS } from 'constants/route-constants';
import shopifyLogo from 'public/images/shopify-logo.png';
import iconPlusSvg from 'public/images/icon-plus.svg';
import {
  SCRIBE_SHOPIFY_EVENTS,
  SCRIBE_SHOPIFY_PARAMS,
  SCRIBE_SHOPIFY_STATES,
} from 'applications/scribe/instrumentations/track';
import { LAST_SELECTED_SHOP_LOCALSTORAGE_KEY } from 'constants/shopify-constants';
import ShopifyTrackEventContext from './track-event';

import styles from './styles.less';

const LINK_NEW_SHOP_KEY = '_LINK_NEW_SHOP_KEY_';

const ShopSelector = ({ shop, shops, setShop, history }) => {
  const { fireShopifyTrackEvent } = useContext(ShopifyTrackEventContext);
  const [dropdownVisible, setDropdownVisible] = useState(false);

  useEffect(
    () => {
      if (!isEmpty(shops) && shop === null) {
        const lastShop = window.localStorage.getItem(
          LAST_SELECTED_SHOP_LOCALSTORAGE_KEY,
        );
        const defaultShop = lastShop
          ? find(shops, s => s.shop === lastShop) || shops[0]
          : shops[0];
        setShop(defaultShop);
      }
    },
    [shops, shop, setShop],
  );

  const onDropdownToggle = useCallback(
    () => {
      if (!dropdownVisible) {
        fireShopifyTrackEvent({
          stateName: SCRIBE_SHOPIFY_STATES.EXPRESS_SHOPIFY_ORDERS_WEB,
          eventName: SCRIBE_SHOPIFY_EVENTS.STORE_NAME_DROPDOWN,
          facets: {
            [SCRIBE_SHOPIFY_PARAMS.TOTAL_STORES]: shops.length,
            [SCRIBE_SHOPIFY_PARAMS.SHOPIFY_STORE_ID_ALL]: shops.map(
              s => s.shop,
            ),
          },
        });
      }
      setDropdownVisible(!dropdownVisible);
    },
    [fireShopifyTrackEvent, setDropdownVisible, shops, dropdownVisible],
  );

  const dropdownHandler = useCallback(
    event => {
      const { key } = event;
      if (LINK_NEW_SHOP_KEY === key) {
        fireShopifyTrackEvent({
          stateName: SCRIBE_SHOPIFY_STATES.EXPRESS_SHOPIFY_ORDERS_WEB,
          eventName: SCRIBE_SHOPIFY_EVENTS.LINK_NEW_STORE,
        });
        history.push(ROUTES_V2_PATHS.shopifyLink);
        return;
      }

      const newShop = find(shops, s => s.shop === key);
      setDropdownVisible(false);
      if (newShop && newShop.name !== shop.name) {
        fireShopifyTrackEvent({
          stateName: SCRIBE_SHOPIFY_STATES.EXPRESS_SHOPIFY_ORDERS_WEB,
          eventName: SCRIBE_SHOPIFY_EVENTS.STORE_NAME,
          facets: {
            [SCRIBE_SHOPIFY_PARAMS.SHOPIFY_STORE_NAME]: newShop.name,
            [SCRIBE_SHOPIFY_PARAMS.SHOPIFY_STORE_ID]: newShop.shop,
          },
        });
        setShop && setShop(newShop);
        window.localStorage.setItem(
          LAST_SELECTED_SHOP_LOCALSTORAGE_KEY,
          newShop.shop,
        );
      }
    },
    [shop, shops, fireShopifyTrackEvent, setDropdownVisible, setShop],
  );

  const dropdownContent = useMemo(
    () =>
      shops && (
        <Menu>
          {shops.map(s => (
            <Menu.Item key={`${s.shop}`} onClick={dropdownHandler}>
              <img
                className={styles.shopifyShopLogo}
                src={shopifyLogo}
                alt={'shopifyLogo'}
              />
              {s.name}
            </Menu.Item>
          ))}
          <Menu.Item key={LINK_NEW_SHOP_KEY} onClick={dropdownHandler}>
            <img
              className={styles.shopifyShopLogo}
              src={iconPlusSvg}
              alt={'icon plus'}
            />
            {I18n.t('shopify.home.shopSelector.linkAnotherStore')}
          </Menu.Item>
        </Menu>
      ),
    [dropdownHandler, shops],
  );

  if (shop === null || shops === null) {
    return null;
  }

  return (
    <div className={styles.shopSelector}>
      <h4 className={styles.shopName}>{shop.name}</h4>
      <Dropdown
        overlay={dropdownContent}
        trigger={['click']}
        placement="bottomLeft"
        overlayClassName={styles.dropdown}
        visible={dropdownVisible}
      >
        <Icon
          type={dropdownVisible ? 'up' : 'down'}
          onClick={onDropdownToggle}
        />
      </Dropdown>
    </div>
  );
};

ShopSelector.defaultProps = {
  shop: null,
  shops: null,
};

ShopSelector.propTypes = {
  history: PropTypes.shape({}).isRequired,
  shop: PropTypes.shape({}),
  shops: PropTypes.arrayOf(PropTypes.shape({})),
  setShop: PropTypes.func.isRequired,
};

const mapDispatchToProps = {
  setShop: setShopAction,
};

export default withRouter(
  connect(
    state => ({
      shop: shopifyShopSelector(state),
      shops: shopifyShopsSelector(state),
    }),
    mapDispatchToProps,
  )(ShopSelector),
);
